import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { Col, Image, Row, Typography, Modal } from 'antd';
import moment from 'moment';

import CustomTable from 'src/components/common/CustomTable';
import CustomButton from 'src/components/common/CustomButton';
import ClassScheduleModal from 'src/components/Modal/ClassScheduleModal';
import ClassRoomModal from 'src/components/Modal/ClassRoomModal';
import AddStudentModal from 'src/components/Modal/AddStudentModal';
import AssignTicketModal from 'src/components/Modal/AssignTicketModal';
import StudentModal from 'src/components/Modal/StudentModal';
import { FieldContents, HalfWrapper, HeaderTitle } from 'src/components/common/Styles';
import { useModal, useLoginTo } from 'src/utils/hooks';

import { customDayOfWeek } from 'src/utils';
import { openNotification } from 'src/components/common/Notification';
import * as queries from 'src/operations/queries';
import * as mutations from 'src/operations/mutations';
import BackButton from 'src/components/common/BackButton';
import { userInfoVar } from 'src/apollo/cache';
import { EditOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { confirm } = Modal;

const ClassDetail = () => {
  const history = useHistory();
  const loginTo = useLoginTo(false);

  const userType = userInfoVar()?.type;

  const { id } = useParams();
  const [classItems, setClassItems] = useState(null);
  const [classStudent, setClassStudent] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const { visible, showModal, handleCancel, popupMode } = useModal();
  const { visible: scheduleVisible, showModal: scheduleModal, handleCancel: handleScheduleCancel } = useModal();
  const { visible: addStudentVisible, showModal: addStudentModal, handleCancel: handleAddStudentCancel } = useModal();
  const { visible: ticketVisible, showModal: ticketShowModal, handleCancel: ticketHandleCancel } = useModal();
  const [userIdx, setUserIdx] = useState(null);
  const { visible: studentEditVisible, showModal: studentEditShowModal, handleCancel: studentHandleCancel } = useModal();

  const { data, refetch } = useQuery(queries.getClass.GET_CLASS_INFO_DETAIL, {
    fetchPolicy: 'no-cache',
    variables: { class_idx: parseInt(id) },
  });
  const [getStudentInfo, { data: studentInfo }] = useLazyQuery(queries.getUser.GET_USER_INFO);

  const {
    data: fetchClassStudent,
    loading: classStudentsLoading,
    refetch: classStudentsRefetch,
  } = useQuery(queries.getClass.CLASS_STUDENT_LIST, {
    fetchPolicy: 'no-cache',
    pollInterval: 3600000,
    variables: { class_idx: parseInt(id) },
  });

  const showStudentEditModal = (idx) => {
    setUserIdx(idx);
    getStudentInfo({
      skip: !userIdx,
      variables: { idx },
    });
    studentEditShowModal();
  };

  const userInfo = useMemo(() => {
    if (studentInfo) {
      return studentInfo?.getUserDataByIdx;
    }
  }, [studentInfo]);

  const [deleteClass] = useMutation(mutations.classRoom.DELETE_CLASS, {
    onCompleted(data) {
      if (data) {
        refetch();
        history.goBack();
        openNotification('삭제 완료!');
      }
    },
    onError(error) {
      console.log('error', error);
    },
  });

  const [removeStudent] = useMutation(mutations.classRoom.REMOVE_STUDENT_IN_CLASS, {
    update(proxy, result) {
      const data = proxy.readQuery({
        query: queries.getClass.CLASS_STUDENT_LIST,
        variables: { class_idx: classItems?.idx },
      });

      const studentList = data.classStudentList.reduce((acc, cur) => {
        if (selectedStudents.includes(cur.user.idx)) {
          return acc;
        }
        acc = [...acc, cur];
        return acc;
      }, []);

      proxy.writeQuery({
        query: queries.getClass.CLASS_STUDENT_LIST,
        variables: { class_idx: classItems?.idx },
        data: {
          classStudentList: [...studentList],
        },
      });
    },
    onCompleted(data) {
      if (data) {
        openNotification('학생 삭제 완료!');
      }
    },
    onError(error) {
      console.log('error', error);
    },
  });

  const deleteConfirm = () => {
    if (id) {
      confirm({
        title: '정말 삭제하시겠습니까?',
        okText: '예',
        okType: 'primary',
        cancelText: '아니오',
        onOk() {
          deleteClass({
            variables: {
              class_idx_list: [Number(id)],
            },
          });
        },
        onCancel() {},
      });
    }
  };

  const deleteStudentConfirm = () => {
    if (selectedStudents.length) {
      confirm({
        title: '정말 삭제하시겠습니까?',
        okText: '예',
        okType: 'primary',
        cancelText: '아니오',
        onOk() {
          removeStudent({
            variables: {
              class_idx: classItems?.idx,
              students: selectedStudents,
            },
          });
        },
        onCancel() {},
      });
    } else {
      infoConfirm();
    }
  };

  function infoConfirm() {
    Modal.warning({
      title: '삭제할 항목을 선택해 주세요.',
      okText: '확인',
    });
  }

  useEffect(() => {
    if (data && data.classInfo) {
      setClassItems(data.classInfo);
    }
  }, [data]);

  const refetchClassItems = async () => {
    const result = await refetch();
    if (result?.data?.classInfo) {
      setClassItems(result.data.classInfo);
    }
    return result;
  };
  useEffect(() => {
    if (fetchClassStudent) {
      setClassStudent(
        fetchClassStudent.classStudentList.reduce((acc, cur, index) => {
          const obj = {
            key: cur.idx,
            name: cur.user.name,
            id: cur.user.id,
            grade: cur.user.school_grade,
            idate: cur.user.idate && moment(cur.user.idate).format('YYYY-MM-DD'),
            etc: Math.floor(Math.random() * Math.floor(2)) === 1 ? '○' : 'X',
            book_buy: Math.floor(Math.random() * Math.floor(2)) === 1 ? '○' : 'X',
            useTicket: Math.floor(Math.random() * Math.floor(2)) === 1 ? '○' : 'X',
            user_idx: cur.user.idx,
            showStudentEditModal,
            loginTo,
          };
          return [...acc, obj];
        }, []),
      );
    }
  }, [classItems?.idx, fetchClassStudent]);

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedStudents(selectedRows.map((data) => data.user_idx));
    },
  };

  const teacherRender = useMemo(() => {
    if (classItems?.class_teacher.length) {
      const teachers = classItems?.class_teacher?.reduce((acc, cur) => {
        if (acc) {
          acc = `${acc}, ${cur?.user?.name}`;
        } else {
          acc = cur?.user?.name;
        }

        return acc;
      }, '');

      return classItems?.class_teacher.lengt >= 3 ? <Text>{teachers}</Text> : teachers;
    }

    return '';
  }, [classItems]);

  return (
    <>
      <Row justify="space-around">
        <Col span={12}>
          <HeaderTitle level={4}>반 관리</HeaderTitle>
        </Col>
        <Col span={12} style={{ paddingTop: 5, textAlign: 'right' }}>
          <BackButton />
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="classroom-box-wrap-height">
        <Col span={12} className="classroom-box1">
          <HalfWrapper className="tblclass-classroom-height">
            <div style={{ background: '#ECF1F3', padding: 10 }}>
              <Row justify="space-between" align="bottom">
                <Col xl={6} md={6}>
                  <Title level={5}>반 정보</Title>
                  <ClassBoookImage book={classItems?.book} />
                </Col>
                <Col xl={11} md={11}>
                  <Row className="halfmanagment-space" gutter={[8, 8]}>
                    <Col>
                      <FieldContents size={0.9} color={'#777777'}>
                        Class Name
                      </FieldContents>
                      <FieldContents color={'#202020'}>{classItems?.name}</FieldContents>
                    </Col>
                  </Row>
                  <Row className="halfmanagment-space" gutter={[8, 8]}>
                    <Col>
                      <FieldContents size={0.9} color={'#777777'}>
                        요일
                      </FieldContents>
                      <FieldContents color={'#202020'}>{classItems?.day_of_week && customDayOfWeek(classItems)}</FieldContents>
                    </Col>
                  </Row>
                  <Row className="halfmanagment-space" gutter={[8, 8]}>
                    <Col>
                      <FieldContents size={0.9} color={'#777777'}>
                        시간
                      </FieldContents>
                      <FieldContents color={'#202020'}>
                        {classItems?.begin_time && classItems?.end_time ? ` ${classItems?.begin_time} ~ ${classItems?.end_time}` : `종일`}
                      </FieldContents>
                    </Col>
                  </Row>
                  <Row className="halfmanagment-space" gutter={[8, 8]}>
                    <Col>
                      <FieldContents size={0.9} color={'#777777'}>
                        학습 시작일
                      </FieldContents>
                      <FieldContents color={'#202020'}>{moment(classItems?.begin_date).format('YYYY-MM-DD')}</FieldContents>
                    </Col>
                    <Col>
                      <FieldContents size={0.9} color={'#777777'}>
                        종료일
                      </FieldContents>
                      <FieldContents color={'#202020'}>
                        {classItems?.end_date && moment(classItems?.end_date).format('YYYY-MM-DD')}
                      </FieldContents>
                    </Col>
                  </Row>
                  <Row className="halfmanagment-space" gutter={[8, 8]}>
                    <Col>
                      <FieldContents size={0.9} color={'#777777'}>
                        강의실
                      </FieldContents>
                      <FieldContents color={'#202020'}>{classItems?.room}</FieldContents>
                    </Col>
                  </Row>

                  <Row className="halfmanagment-space" gutter={[8, 8]}>
                    <Col>
                      <FieldContents size={0.9} color={'#777777'}>
                        강사명
                      </FieldContents>
                      <FieldContents>{teacherRender}</FieldContents>
                    </Col>
                  </Row>
                </Col>

                <Col xl={7} md={24} sm={24}>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Text type="secondary">Syllabus( 학사 일정 )</Text>
                      <CustomButton
                        style={{ background: '#FDB40A', width: '100%', marginBottom: 20 }}
                        type="primary"
                        onClick={scheduleModal}
                      >
                        일정수정
                      </CustomButton>
                    </Col>
                  </Row>
                  {userType === 2 && (
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <CustomButton danger style={{ marginRight: 10, width: '100%' }} onClick={deleteConfirm}>
                          삭제
                        </CustomButton>
                      </Col>
                      <Col span={12}>
                        <CustomButton style={{ marginRight: 10, width: '100%' }} onClick={() => showModal('edit')} type="primary" ghost>
                          수정
                        </CustomButton>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            </div>
          </HalfWrapper>
        </Col>

        <Col span={12} className="classroom-box2">
          <HalfWrapper className="tblclass-classroom-height">
            <Row>
              <Col span={24}>
                <CustomTable
                  loading={classStudentsLoading}
                  dataSource={classStudent}
                  columns={studingColumns}
                  // rowSelection={{
                  //   type: 'checkbox',
                  //   ...rowSelection,
                  // }}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  color="#edf3fb"
                  scroll={{ y: 'calc(100vh - 254px)' }}
                />
              </Col>
            </Row>

            <Row justify="end" gutter={[8, 8]}>
              <Col lg={12} md={12} xs={24}>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    {/* <CustomButton
                      danger
                      style={{
                        marginRight: 10,
                        width: '100%',
                      }}
                      onClick={deleteStudentConfirm}
                    >
                      삭제
                    </CustomButton> */}
                  </Col>
                  <Col span={12}>
                    <CustomButton
                      style={{
                        marginRight: 10,
                        border: '1px solid #52c419',
                        width: '100%',
                      }}
                      onClick={addStudentModal}
                    >
                      <Text type={'success'}>학생 추가/삭제</Text>
                    </CustomButton>
                  </Col>
                </Row>
              </Col>
            </Row>
          </HalfWrapper>
        </Col>
      </Row>

      <ClassRoomModal
        classItems={classItems}
        visible={visible}
        popupMode={popupMode}
        handleCancel={handleCancel}
        refetch={refetch}
        classStudents={classStudent}
      />

      <ClassScheduleModal
        visible={scheduleVisible}
        handleCancel={handleScheduleCancel}
        class_idx={classItems?.idx}
        classItems={classItems}
        refetchClass={refetch}
      />

      <AddStudentModal
        visible={addStudentVisible}
        handleCancel={handleAddStudentCancel}
        class_idx={classItems?.idx}
        classStudentsRefetch={classStudentsRefetch}
      />

      <AssignTicketModal visible={ticketVisible} handleCancel={ticketHandleCancel} />

      <StudentModal popupMode="edit" handleCancel={studentHandleCancel} visible={studentEditVisible} userInfo={userInfo} />
    </>
  );
};

export default ClassDetail;

const ClassBoookImage = ({ book }) => {
  const path = useMemo(() => {
    if (book?.book_sub) {
      return book.book_sub.reduce((acc, cur) => {
        if (cur?.no === 1 && cur?.book_category?.no === 10) {
          return cur?.cover_path;
        }
        return acc;
      }, '');
    }
    return '';
  }, [book]);
  const BASE_CONTENT_PATH = 'https://cdn.cloubot.com';
  return (
    <Image
      width={120}
      height={150}
      src={`${BASE_CONTENT_PATH}${path}`}
      fallback="data:image/png;base64,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"
    />
  );
};

const studingColumns = [
  {
    title: '이름',
    align: 'center',
    dataIndex: 'name',
  },
  {
    title: 'ID',
    align: 'center',
    dataIndex: 'id',
    render: (text, record) => {
      return (
        <span>
          {text}&nbsp;
          <CustomButton
            onClick={() => {
              record.showStudentEditModal(record.user_idx);
            }}
            size="small"
            icon={<EditOutlined />}
          />
        </span>
      );
    },
  },
  {
    title: '학년',
    align: 'center',
    dataIndex: 'grade',
  },
  {
    title: '학생화면보기',
    align: 'center',
    dataIndex: 'view',
    render: (_, record) => {
      return (
        <CustomButton
          onClick={() => {
            record.loginTo(record.id);
          }}
          type="primary"
          size="small"
        >
          로그인
        </CustomButton>
      );
    },
  },
  // {
  //   title: '입학일',
  //   align: 'center',
  //   dataIndex: 'idate',
  // },
  // {
  //   title: '기타 컨텐츠',
  //   align: 'center',
  //   dataIndex: 'etc',
  // },
  // {
  //   title: '교재 구매',
  //   align: 'center',
  //   dataIndex: 'book_buy',
  // },
  // {
  //   title: '이용권',
  //   align: 'center',
  //   dataIndex: 'useTicket',
  // },
];
